import { useEffect, useMemo, useState } from "react";
import { useAuthSelector } from "@risalabs_frontend_org/auth-wrapper";
import { useDispatch } from "react-redux";
import {
  CheckBox,
  controlToastState,
  Modal,
  Select,
  TextInput,
} from "risa-oasis-ui_v2";

import { useSelector } from "react-redux";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import { HealthCareFacilityModel } from "../../../data-model/healthFacility";
import { HealthCareFacilityFeatures } from "../../../enums/screenNames";
import { getStateLabelValueArray } from "../../../enums/usStates";
import { fetchClientFromFirebase } from "../../../redux/slice/clientConfiguration";
import { PlanDetails } from "../../../redux/slice/facilitySlice";
import { fetchHealthFacilityFromFirebase } from "../../../redux/slice/healthFacilitySlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import CustomRadioButton from "../../customRadioButton/customRadioButton";

interface NewFacilityModalProps {
  data: HealthCareFacilityModel | null;
  index?: number;
  onModalClose?: (boolean) => void;
  type: "add" | "edit";
  plans?: PlanDetails | null;
}

const NewFacilityModal = (props: NewFacilityModalProps) => {
  const { data: clientName } = useSelector(
    (state: RootState) => state.clientConfiguration,
  );

  const { data: healthFacility } = useSelector(
    (state: RootState) => state.healthFacility,
  );

  const [resetFormData, setResetFormData] = useState(false);
  const [loading, setLoading] = useState(false);

  const checkBoxSelectedByDefault = {
    authReview: false,
    coverageCheck: false,
    medicalNeccessityChecklist: false,
    paSubmission: false,
    pharmaPa: false,
  };
  const initialData: HealthCareFacilityModel = {
    client_id: "",
    Name: "",
    NPI: "",
    TaxID: "",
    PhoneNumber: "",
    FhirId: "",
    is_external_organization: false,

    Address: {
      StreetAddress: "",
      City: "",
      State: "",
      ZIP: "",
    },
    emr_name: "",
    fhir_client: "",
    RisaFaxNumber: "",
    RisaPhoneNumber: "",
    updated_at: "",
    updated_by: "",
  };

  const [facilityData, setFacilityData] =
    useState<HealthCareFacilityModel>(initialData);

  const [checkBoxValues, setCheckBoxValues] = useState(
    checkBoxSelectedByDefault,
  );

  const { user } = useAuthSelector((state) => state.firebaseAuthentication,
  );

  useEffect(() => {
    dispatch(fetchClientFromFirebase());
  }, []);

  const dispatch = useDispatch<AppDispatch>();
  const emrOptions = useMemo(() => {
    return [
      {
        label: "Onco EMR",
        value: "oncoemr",
      },
    ];
  }, []);

  const clientOptions = useMemo(() => {
    return clientName.map((client) => ({
      label: client.name ?? "",
      value: client.DocId ?? "",
    }));
  }, [clientName]);

  const organizationOptions = useMemo(() => {
    return healthFacility.map((facility) => ({
      label: facility.Name ?? "",
      value: facility.DocId ?? "",
    }));
  }, [healthFacility]);

  useEffect(() => {
    if (props.plans && props.type === "edit") {
      const updatedState = { ...checkBoxSelectedByDefault };
      const plans = props.plans?.feature ?? [];
      const organizations = props.plans?.internal_organizations ?? [];

      plans?.forEach((plan) => {
        updatedState[plan as keyof typeof updatedState] = true;
      });

      organizations?.forEach((organization) => {
        if (organization.facility_id) {
          updatedState[organization.facility_id] = true;
        }
      });

      setCheckBoxValues(updatedState);
    }
  }, [props.plans]);

  const areAllFieldsFilled = (): boolean => {
    const {
      Name,
      NPI,
      TaxID,
      PhoneNumber,
      Address,
      FhirId,
      emr_name,
      fhir_client,
      RisaPhoneNumber,
      RisaFaxNumber,
      client_id,
    } = facilityData;

    if (
      !Name ||
      !NPI ||
      !TaxID ||
      !PhoneNumber ||
      !FhirId ||
      !emr_name ||
      !fhir_client ||
      !RisaPhoneNumber ||
      !RisaFaxNumber ||
      !client_id
    ) {
      return false;
    }

    // if (is_external_organization && !organization_id) {
    //   return false;
    // }

    for (const field in Address) {
      if (!Address[field as keyof typeof Address]) {
        return false;
      }
    }

    if (!Object.values(checkBoxValues).some((value) => value === true)) {
      return false;
    } else {
      return true;
    }
  };

  const handleClientSelection = (selectedData) => {
    const { value } = selectedData;

    const selectedClient = clientName.find((client) => client.DocId === value);

    if (selectedClient) {
      setFacilityData((prev) => ({
        ...prev,
        client_id: value,
        NPI: selectedClient.NPI || "",
        TaxID: selectedClient.TaxID || "",
        PhoneNumber: selectedClient.PhoneNumber || "",
        FhirId: selectedClient.FhirId || "",
        Address: {
          StreetAddress: selectedClient.Address?.StreetAddress || "",
          City: selectedClient.Address?.City || "",
          State: selectedClient.Address?.State || "",
          ZIP: selectedClient.Address?.ZIP || "",
        },
        emr_name: selectedClient.emr_name || "",
        fhir_client: selectedClient.fhir_client || "",
        RisaFaxNumber: selectedClient.RisaFaxNumber || "",
        RisaPhoneNumber: selectedClient.RisaPhoneNumber || "",
      }));
    }
  };

  const handleInputChangeForPrimaryDetails = (data) => {
    const { name, value } = data;
    setFacilityData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeForAddressDetails = (data) => {
    const { name, value } = data;
    setFacilityData((prev) => ({
      ...prev,
      Address: {
        ...prev.Address,
        [name]: value,
      },
    }));
  };

  const handleOnClose = () => {
    props.onModalClose && props.onModalClose(false);
  };

  const checkBoxOptions = Object.entries(HealthCareFacilityFeatures).map(
    ([key, value]) => ({
      label: value,
      value: key,
    }),
  );

  const addFacilityToDb = async () => {
    try {
      setLoading(true);

      // Separate features and organizations
      const selectedFeatures: string[] = [];
      const selectedOrganizations: Array<{
        facility_id: string;
        facility_name: string;
        audit_pdf_rpa_run: boolean;
      }> = [];

      Object.entries(checkBoxValues).forEach(([key, value]) => {
        if (value) {
          // Check if this is a feature (from HealthCareFacilityFeatures enum)
          const isFeature = Object.keys(HealthCareFacilityFeatures).includes(
            key,
          );
          if (isFeature) {
            selectedFeatures.push(key);
          } else {
            // This is an organization ID
            selectedOrganizations.push({
              facility_id: key,
              facility_name:
                healthFacility.find((f) => f.DocId === key)?.Name || "",
              audit_pdf_rpa_run: false,
            });
          }
        }
      });

      const response = await FirestoreService.addDocument(
        FirestoreCollectionReference.healthcareFacility(),
        {
          ...facilityData,
          dashboard_type: "pharmacy",
          updated_by: user?.email,
        },
      );

      const newId = response["id"] ?? "";

      const featuresData = {
        feature: selectedFeatures,
        ...(facilityData.is_external_organization && {
          internal_organizations: selectedOrganizations,
        }),
        type: "basic",
      };

      await FirestoreService.updateDocument(
        FirestoreCollectionReference.healthcareFacility(),
        newId,
        {
          DocId: newId,
        },
      );

      await FirestoreService.addDocumentWithId(
        FirestoreCollectionReference.healthFacilityPatients(newId),
        {
          id: "0000",
        },
      );

      await FirestoreService.addDocumentWithId(
        FirestoreCollectionReference.healthFacilityProviders(newId),
        {
          id: "0000",
        },
      );

      const planData = await FirestoreService.addDocument(
        FirestoreCollectionReference.healthFacilityPlan(newId),
        {
          ...featuresData,
        },
      );

      await FirestoreService.updateDocument(
        FirestoreCollectionReference.healthFacilityPlan(newId),
        planData["id"],
        {
          planId: planData["id"],
        },
      );

      dispatch(fetchHealthFacilityFromFirebase());
      controlToastState("facility-added-success");
      handleOnClose();
    } catch (error) {
      console.error("Error adding facility to firebase", error);
      controlToastState("facility-added-failure");
    } finally {
      setLoading(false);
    }
  };

  const updateFacilityInDb = async () => {
    setLoading(true);
    try {
      await FirestoreService.updateDocument(
        FirestoreCollectionReference.healthcareFacility(),
        props.data?.DocId || "",
        {
          ...props?.data,
          ...facilityData,
          updated_by: user?.email,
          updated_at: new Date().toISOString(),
        },
      );
      // Separate features and organizations for update
      const selectedFeatures: string[] = [];
      const selectedOrganizations: Array<{
        facility_id: string;
        facility_name: string;
        audit_pdf_rpa_run: boolean;
      }> = [];

      Object.entries(checkBoxValues).forEach(([key, value]) => {
        if (value) {
          // Check if this is a feature (from HealthCareFacilityFeatures enum)
          const isFeature = Object.keys(HealthCareFacilityFeatures).includes(
            key,
          );
          if (isFeature) {
            selectedFeatures.push(key);
          } else {
            // This is an organization ID
            selectedOrganizations.push({
              facility_id: key,
              facility_name:
                healthFacility.find((f) => f.DocId === key)?.Name || "",
              audit_pdf_rpa_run: false,
            });
          }
        }
      });

      await FirestoreService.updateDocument(
        FirestoreCollectionReference.facilityPlan(props.data?.DocId || ""),
        props.plans?.planId || "",
        {
          feature: selectedFeatures,
          ...(facilityData.is_external_organization && {
            internal_organizations: selectedOrganizations,
          }),
          type: "basic",
          planId: props.plans?.planId || "",
        },
      );
    } catch (error) {
      console.error("Error updating facility to firebase", error);
    } finally {
      setLoading(false);
      dispatch(fetchHealthFacilityFromFirebase());
      handleOnClose();
    }
  };

  const onHandleCheckBoxChange = (data) => {
    const { name, value } = data;
    setCheckBoxValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (resetFormData) {
      setResetFormData(false);
    }
  }, [facilityData]);

  useEffect(() => {
    if (props.data) {
      const newData: HealthCareFacilityModel = {
        client_id: props?.data?.client_id || "",
        Name: props?.data?.Name ?? "",
        NPI: props?.data?.NPI ?? "",
        TaxID: props?.data?.TaxID ?? "",
        PhoneNumber: props?.data?.PhoneNumber ?? "",
        FhirId: props?.data?.FhirId ?? "",
        is_external_organization:
          props?.data?.is_external_organization ?? false,
        Address: {
          StreetAddress: props.data.Address?.StreetAddress || "",
          City: props?.data?.Address?.City ?? "",
          State: props?.data?.Address?.State ?? "",
          ZIP: props?.data?.Address?.ZIP ?? "",
        },
        emr_name: props?.data?.emr_name ?? "",
        fhir_client: props?.data?.fhir_client ?? "",
        RisaFaxNumber: props?.data?.RisaFaxNumber ?? "",
        RisaPhoneNumber: props?.data?.RisaPhoneNumber ?? "",
        updated_at: props?.data?.updated_at || "",
        updated_by: props?.data?.updated_by || "",
      };
      setFacilityData(newData);
    }
  }, [props.data]);

  return (
    <Modal
      dialogId={"new-facility-modal"}
      onSave={props.type === "add" ? addFacilityToDb : updateFacilityInDb}
      title={props.type === "add" ? "Onboard New Facility" : "Edit Facility"}
      saveButtonText={loading ? "Saving..." : "Save"}
      cancelText={"Cancel"}
      heightPercentage={70}
      disableSave={!areAllFieldsFilled() || loading}
      onClose={handleOnClose}
    >
      <div className="facility-form-container">
        <form>
          <fieldset>
            <legend>Primary Details of Facility</legend>
            <Select
              id={"Client"}
              label={"Client"}
              placeholder={"Select a client ..."}
              required
              onOptionChange={handleClientSelection}
              error={
                facilityData.client_id === "" ? "Please provide a client" : ""
              }
              defaultValue={facilityData.client_id}
              options={clientOptions}
            />
            <div className="flex flex-col gap-2 text-xs">
              Is External Facility?
              <div className="flex flex-row gap-2">
                <CustomRadioButton
                  name="isExternalFacility"
                  value="true"
                  checked={facilityData.is_external_organization === true}
                  label="Yes"
                  onChange={(event) =>
                    handleInputChangeForPrimaryDetails({
                      name: "is_external_organization",
                      value: event.target.value === "true",
                    })
                  }
                />
                <CustomRadioButton
                  name="isExternalFacility"
                  value="false"
                  checked={facilityData.is_external_organization === false}
                  label="No"
                  onChange={(event) =>
                    handleInputChangeForPrimaryDetails({
                      name: "is_external_organization",
                      value: event.target.value === "true",
                    })
                  }
                />
              </div>
            </div>
            <TextInput
              id={"Name"}
              label={"Name of Facility Center"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={facilityData.Name === "" ? "Please provide a name" : ""}
              resetField={resetFormData}
              defaultValue={facilityData.Name}
            />
            <TextInput
              id={"NPI"}
              label={"NPI"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={facilityData.NPI === "" ? "Please provide an NPI" : ""}
              resetField={resetFormData}
              defaultValue={facilityData.NPI}
            />
            <TextInput
              id={"TaxID"}
              label={"Tax ID"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={facilityData.TaxID === "" ? "Please provide a Tax ID" : ""}
              resetField={resetFormData}
              defaultValue={facilityData.TaxID}
            />
            <TextInput
              id={"FhirId"}
              label={"FHIR ID"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={
                facilityData?.FhirId === "" || facilityData?.FhirId == null
                  ? "Please provide a FHIR ID"
                  : ""
              }
              resetField={resetFormData}
              defaultValue={facilityData?.FhirId ?? ""}
            />
            <TextInput
              id={"PhoneNumber"}
              label={"Phone Number"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={
                facilityData.PhoneNumber === ""
                  ? "Please provide a phone number"
                  : ""
              }
              resetField={resetFormData}
              defaultValue={facilityData.PhoneNumber}
            />
            <Select
              id={"emr_name"}
              label={"EMR Name"}
              options={emrOptions}
              onOptionChange={handleInputChangeForPrimaryDetails}
              placeholder={"Select an EMR ..."}
              required
              defaultValue={facilityData?.emr_name}
              error={
                facilityData?.emr_name === "" ? "Please select an EMR" : ""
              }
            />
            <TextInput
              id={"fhir_client"}
              label={"FHIR Client"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={
                facilityData?.fhir_client === ""
                  ? "Please provide a FHIR client"
                  : ""
              }
              defaultValue={facilityData?.fhir_client}
            />
            <TextInput
              id={"RisaPhoneNumber"}
              label={"RISA Phone Number"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={
                facilityData?.RisaPhoneNumber === ""
                  ? "Please provide a phone number"
                  : ""
              }
              defaultValue={facilityData?.RisaPhoneNumber}
            />
            <TextInput
              id={"RisaFaxNumber"}
              label={"RISA Fax Number"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={
                facilityData?.RisaFaxNumber === ""
                  ? "Please provide a fax number"
                  : ""
              }
              defaultValue={facilityData?.RisaFaxNumber}
            />
          </fieldset>
          <fieldset>
            <legend>Address Details of Facility</legend>
            <TextInput
              id={"StreetAddress"}
              label={"Street Address"}
              required
              onChange={handleChangeForAddressDetails}
              error={
                facilityData?.Address?.StreetAddress === ""
                  ? "Please provide a street address"
                  : ""
              }
              resetField={resetFormData}
              defaultValue={facilityData?.Address?.StreetAddress}
            />
            <TextInput
              id={"City"}
              label={"City"}
              required
              onChange={handleChangeForAddressDetails}
              error={
                facilityData?.Address?.City === ""
                  ? "Please provide a city"
                  : ""
              }
              resetField={resetFormData}
              defaultValue={facilityData?.Address?.City}
            />
            <Select
              id={"State"}
              onOptionChange={handleChangeForAddressDetails}
              label={"State"}
              placeholder={"Select a state ..."}
              options={getStateLabelValueArray()}
              required
              error={
                facilityData?.Address?.State === ""
                  ? "Please select a state"
                  : ""
              }
              resetValue={resetFormData}
              defaultValue={facilityData?.Address?.State}
            />
            <TextInput
              id={"ZIP"}
              label={"Zip Code"}
              required
              onChange={handleChangeForAddressDetails}
              error={
                facilityData?.Address?.ZIP === ""
                  ? "Please provide a zip code"
                  : ""
              }
              resetField={resetFormData}
              defaultValue={facilityData?.Address?.ZIP}
            />
          </fieldset>

          <fieldset>
            <legend>Plans for the facility</legend>
            {checkBoxOptions.map((option) => (
              <CheckBox
                label={option.label}
                id={option.value}
                defaultValue={checkBoxValues[option.value]}
                onCheckBoxValueChange={(data) => onHandleCheckBoxChange(data)}
              />
            ))}
          </fieldset>
          {facilityData.is_external_organization && (
            <fieldset>
              <legend>Organizations for the facility</legend>
              {organizationOptions.map((option) => (
                <CheckBox
                  label={option.label}
                  id={option.value}
                  defaultValue={checkBoxValues[option.value]}
                  onCheckBoxValueChange={(data) => onHandleCheckBoxChange(data)}
                />
              ))}
            </fieldset>
          )}
        </form>
      </div>
    </Modal>
  );
};

export default NewFacilityModal;
